import './index.css'
import cn from 'clsx'
import React, { useContext } from 'react'
import { connect, useSelector } from 'react-redux'
import { NestingVersionsContext } from 'contexts/nesting-versions'
import { ShortlistContext } from 'contexts/shortlist'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import ButtonContainer from 'components/ui/button-container'
import Flex from 'components/ui/flex'
import { HoverContext } from 'components/ui/hover-provider'
import PaneItemIndex from 'components/ui/pane-item-index'
import Tooltip from 'components/ui/tooltip'
import UseWithCautionDeferredRender from 'components/ui/use-with-caution-deferred-render'

const stopPropagation = (event: React.SyntheticEvent<any>) =>
  event.stopPropagation()

const mapStateToProps = (state): any => {
  return {
    height: state.user.settings.explorer_component_height || 'Default',
  }
}

export type PaneItemAppearance =
  | 'default'
  | 'disabled'
  | 'active'
  | 'selected'
  | 'player'
  | 'modal'
type Props = {
  actions?: React.ReactNode
  appearance?: PaneItemAppearance
  bar?: React.ReactNode | null | undefined
  content: React.ReactNode
  dataTestId?: string
  disabled?: boolean
  height: 'Compact' | 'Default'
  // index - numbering in shortlists
  index?: number
  media?: React.ReactNode
  title: React.ReactNode
  tooltip?: string | null | undefined
  onClick?: (() => unknown) | null | undefined
  nodeRight?: React.ReactNode
  className?: string
}

/**
 * A versatile pane item component that displays content with optional actions, media, and interactive states
 * @param actions - Action buttons or elements to display on hover/interaction
 * @param appearance - Visual appearance state of the pane item (default: 'default')
 * @param bar - Additional bar content to display at the bottom of the item
 * @param content - Main content to display in the pane item
 * @param disabled - Whether the pane item is disabled
 * @param height - Display height variant from user settings
 * @param index - Index number for shortlist numbering
 * @param media - Media content to display alongside the main content
 * @param title - Title content for the pane item
 * @param tooltip - Tooltip text to display on hover
 * @param onClick - Click handler for the pane item
 * @param nodeRight - Additional content to display on the right side
 * @returns Interactive pane item with configurable appearance and content areas
 */
const PaneItem = (props: Props) => {
  const {
    actions,
    bar,
    dataTestId,
    tooltip,
    disabled,
    height,
    index,
    media,
    title,
    onClick,
    nodeRight,
    className: customClassName,
  } = props
  let { appearance } = props

  if (disabled) {
    appearance = 'disabled'
  }

  if (!appearance) appearance = 'default'
  const nestingVersionsContext = useContext(NestingVersionsContext)
  const shortlistContext = useContext(ShortlistContext)
  const isPhone = useSelector(isDeviceTypePhone)
  const { isHover } = useContext(HoverContext)
  const className = cn(
    'pane-item',
    {
      'pane-item--active': isPhone ? false : appearance === 'active',
      'pane-item--default': appearance === 'default',
      'pane-item--selected': appearance === 'selected',
      'pane-item--disabled': disabled,
      'pane-item--hover': isPhone ? false : isHover,
      'component-height--Compact': height === 'Compact',
      'component-height--Default': height === 'Default',
    },
    customClassName,
  )
  const showIndex =
    index !== undefined ||
    (nestingVersionsContext?.nesting && Boolean(shortlistContext?.shortlistId))
  return (
    <Tooltip content={tooltip}>
      <div
        className={className}
        data-testid={dataTestId ?? 'pane-item'}
        onClick={onClick ?? undefined}
      >
        {showIndex && (
          <PaneItemIndex
            index={!nestingVersionsContext?.nesting ? index : undefined}
          />
        )}
        <div className="pane-item-content">
          <Flex spacing="medium">
            <Flex.Content>{props.content}</Flex.Content>
            {nodeRight}
          </Flex>
        </div>

        {actions && (
          <div
            className={cn('pane-item-actions', {
              'pane-item-actions--visible': isPhone ? true : isHover,
            })}
            onClick={stopPropagation}
          >
            <ButtonContainer>
              <UseWithCautionDeferredRender>
                {actions}
              </UseWithCautionDeferredRender>
            </ButtonContainer>
          </div>
        )}
        {bar}
      </div>
    </Tooltip>
  )
}

export default connect(mapStateToProps)(PaneItem) as any
